/**
 * Voice AI — Phone Number Service
 * Owns all phone-numbers API calls and data formatting.
 * Components call this service instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class PhoneService {

  /* ─── API Calls ─── */

  /**
   * Generic phone-numbers API call.
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
   * @param {Object|null} body - JSON body
   * @param {string} query - Query string (e.g., '?id=5')
   * @returns {Promise<Object>} Parsed JSON response
   */
  async apiCall(method, body = null, query = '', { signal } = {}) {
    const url = `${API_BASE}/phone-numbers.php${query}`;
    const opts = { method };
    if (signal) opts.signal = signal;
    if (body) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    }
    const res = await authService.apiFetch(url, opts);
    return res.json();
  }

  /**
   * Load phone numbers with pagination, search, and sort.
   * @param {Object} params - { page, pageSize, search, sortColumn, sortDirection }
   * @returns {Promise<Object>} { success, numbers, pagination }
   */
  async loadNumbers({ page = 1, pageSize = 20, search = '', sortColumn = '', sortDirection = 'asc' } = {}, { signal } = {}) {
    const params = new URLSearchParams({ page, limit: pageSize });
    if (search) params.set('search', search);
    if (sortColumn) {
      const sortMap = { phone: 'twilio_number', label: 'label', agent: 'agent_name', status: 'status', cost: 'monthly_cost' };
      params.set('sort', sortMap[sortColumn] || sortColumn);
      params.set('direction', sortDirection);
    }
    return this.apiCall('GET', null, `?${params.toString()}`, { signal });
  }

  /**
   * Create a new phone number.
   * @param {Object} data - Phone number data
   * @returns {Promise<Object>} { success, id }
   */
  async createNumber(data) {
    return this.apiCall('POST', data);
  }

  /**
   * Update an existing phone number.
   * @param {Object} data - Must include `id`
   * @returns {Promise<Object>} { success }
   */
  async updateNumber(data) {
    return this.apiCall('PUT', data);
  }

  /**
   * Delete a phone number.
   * @param {number} id - Phone number ID
   * @returns {Promise<Object>} { success }
   */
  async deleteNumber(id) {
    return this.apiCall('DELETE', null, `?id=${id}`);
  }

  /**
   * Assign an agent to a phone number.
   * @param {number} numberId - Phone number ID
   * @param {number|null} agentId - Agent ID or null to unassign
   * @returns {Promise<Object>} { success }
   */
  async assignAgent(numberId, agentId) {
    return this.apiCall('PUT', { id: numberId, agent_id: agentId ? parseInt(agentId) : null });
  }

  /**
   * Save notes for a phone number.
   * @param {number} id - Phone number ID
   * @param {string} notes - Notes text
   * @returns {Promise<Object>} { success }
   */
  async saveNotes(id, notes) {
    return this.apiCall('PUT', { id, notes });
  }

  /**
   * Load agents list (active only).
   * @returns {Promise<Array>} Array of active agents
   */
  async loadAgents() {
    const res = await authService.apiFetch(`${API_BASE}/agents.php`);
    const data = await res.json();
    return (data.agents || []).filter(a => a.status === 'active');
  }

  /* ─── Data Formatting ─── */

  /**
   * Format API response number → UI-friendly object.
   * @param {Object} n - Raw number from API
   * @returns {Object} Formatted number for UI
   */
  formatForUI(n) {
    return {
      id: n.id,
      phone: this.formatPhone(n.twilio_number),
      raw: n.twilio_number,
      twilioSid: n.twilio_sid || '',
      agentId: n.agent_id || 0,
      country: n.country_code || 'US',
      flag: this.getFlag(n.country_code || 'US'),
      type: n.number_type || 'Local',
      label: n.label || '',
      agent: {
        name: n.agent_name || 'Unassigned',
        initials: this.getInitials(n.agent_name)
      },
      ujid: n.ujid || 0,
      park: n.ujid ? `UJID-${n.ujid}` : '',
      parkName: n.park_name || '',
      status: n.status || 'inactive',
      monthlyCost: n.monthly_cost || 0,
      callerIdName: n.caller_id_name || '',
      callerIdEnabled: !!n.caller_id_enabled,
      notes: n.notes || '',
      inboundHours: this.formatHoursForUI(n.business_hours),
      businessHoursRaw: n.business_hours || {},
      timezone: n.timezone || 'America/New_York',
      routingRules: n.routing_rules || '',
      createdAt: n.created_at,
      updatedAt: n.updated_at
    };
  }

  /**
   * Format E.164 phone to display format.
   * +1XXXXXXXXXX → +1 (XXX) XXX-XXXX
   */
  formatPhone(raw) {
    if (!raw) return '';
    const match = raw.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    return raw;
  }

  /** Country code → flag emoji */
  getFlag(code) {
    const flags = { US: '\u{1F1FA}\u{1F1F8}', CA: '\u{1F1E8}\u{1F1E6}', GB: '\u{1F1EC}\u{1F1E7}', MX: '\u{1F1F2}\u{1F1FD}' };
    return flags[code] || '\u{1F30E}';
  }

  /** Name → 2-letter initials */
  getInitials(name) {
    if (!name) return '--';
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  /** Business hours object → UI display array */
  formatHoursForUI(bh) {
    if (!bh || typeof bh !== 'object' || Object.keys(bh).length === 0) return [];
    const dayMap = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const result = [];
    for (const day of dayOrder) {
      if (!bh[day]) continue;
      const d = bh[day];
      if (d.closed) {
        result.push({ day: dayMap[day], time: 'Closed' });
      } else if (d.open === '00:00' && d.close === '23:59') {
        result.push({ day: dayMap[day], time: '24 hours' });
      } else {
        result.push({ day: dayMap[day], time: `${this._to12h(d.open)} - ${this._to12h(d.close)}` });
      }
    }
    return result;
  }

  /** 24h time → 12h (e.g., "14:30" → "2:30 PM") */
  _to12h(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
}

export const phoneService = new PhoneService();
